import { Question } from 'app/models/types';
import { FieldNames } from 'app/models/fields/names';
import { SHARED } from 'app/models/questions/shared';
import { TextInput } from 'app/components/FormFields/TextInput';
import { Currency } from 'app/components/FormFields/Currency';
import { SwitchToggle } from 'app/components/FormFields/SwitchToggle';
import { DynamicLeaseOwnerComponent } from 'app/components/FormFieldArrays/Dynamic/DynamicLeaseOwner';
import { Address } from 'app/components/FormFields/Address';
import { State } from 'app/components/FormFields/State';
import { ZipCode } from 'app/components/FormFields/ZipCode';
import { Select } from 'app/components/FormFields/Select';
import { SinglePastDate } from 'app/components/FormFields/SinglePastDate';
import { Email } from 'app/components/FormFields/Email';
import { BusinessName } from 'app/components/FormFields/BusinessName';
import { Name } from 'app/components/FormFields/Name';
import { Phone } from 'app/components/FormFields/Phone';
import {
  COMMERCIAL_ENTITY_TYPE_OPTIONS,
  NAME_SUFFIX_OPTIONS,
  STATE_OPTIONS,
} from 'app/models/options/options';

export const getCommercialLeaseEquipmentQuestionByName = (name: string): Question => {
  return COMMERCIAL_LEASE_EQUIPMENT_QUESTIONS.find((question) => {
    return question.name === name;
  }) as Question;
};

export const getCommercialLeaseEquipmentQuestionsByName = (names: string[] = []): Question[] => {
  return names.map(getCommercialLeaseEquipmentQuestionByName);
};

export const COMMERCIAL_LEASE_EQUIPMENT_QUESTIONS: Question[] = [
  {
    id       : 1001,
    component: BusinessName,
    label    : 'Business Name',
    name     : FieldNames.commercialBusinessName,
  },
  {
    id       : 1002,
    component: SinglePastDate,
    label    : 'Business Start Date',
    name     : FieldNames.commercialBusinessStartDate,
  },
  {
    id       : 1003,
    component: Address,
    label    : 'Street Address',
    name     : FieldNames.commercialStreet,
    title    : 'Where is the business located?',
  },
  {
    id        : 1004,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.commercialStreet2,
  },
  {
    id       : 1005,
    component: Address,
    label    : 'City',
    name     : FieldNames.commercialCity,
  },
  {
    id       : 1006,
    component: State,
    label    : 'State',
    name     : FieldNames.commercialState,
  },
  {
    id       : 1007,
    component: ZipCode,
    label    : 'Zip Code',
    name     : FieldNames.commercialZip,
  },
  {
    id       : 1008,
    component: TextInput,
    label    : 'County',
    name     : FieldNames.commercialBusinessCounty,
  },
  {
    ...SHARED[FieldNames.phone],
    id  : 1009,
    name: FieldNames.commercialPhone,
  },
  {
    id       : 1010,
    component: Email,
    label    : 'Business Email',
    name     : FieldNames.commercialBusinessEmail,
  },
  {
    id      : 1011,
    component: Select,
    label   : 'Entity Type',
    name    : FieldNames.commercialEntityType,
    options : COMMERCIAL_ENTITY_TYPE_OPTIONS,
  },
  {
    id       : 1012,
    component: Select,
    label    : 'State of Organization',
    name     : FieldNames.commercialStateOfOrganization,
    options  : STATE_OPTIONS,
  },
  {
    id       : 1013,
    component: TextInput,
    label    : 'Federal Tax ID#',
    name     : FieldNames.commercialTIN,
  },
  {
    id       : 1014,
    component: TextInput,
    label    : 'Company Description',
    name     : FieldNames.commercialDescription,
  },
  {
    id       : 1015,
    component: DynamicLeaseOwnerComponent,
    name     : FieldNames.commercialOwnerList,
  },
  {
    id       : 1016,
    component: TextInput,
    label    : 'Equipment Description',
    name     : FieldNames.commercialEquipmentDescription,
  },
  {
    id       : 1017,
    component: Currency,
    label    : 'Equipment Estimated Cost',
    name     : FieldNames.commercialExpectedCost,
  },
  {
    id       : 1018,
    component: TextInput,
    label    : 'Equipment Location',
    name     : FieldNames.commercialEquipmentLocation,
  },
  {
    id       : 1019,
    component: SwitchToggle,
    label    : 'Lessee Sales Tax Exempt?',
    name     : FieldNames.commercialSalesTaxExempt,
  },
  {
    id       : 1020,
    component: TextInput,
    label    : 'Tax Exempt Reason',
    name     : FieldNames.commercialTaxExemptReason,
    showIf   : [(values) => values[FieldNames.commercialSalesTaxExempt] === true],
  },
  {
    id       : 1021,
    component: SwitchToggle,
    label    : 'Lessee Property Tax Exempt?',
    name     : FieldNames.commercialPropertyTaxExempt,
  },
  {
    id       : 1022,
    component: TextInput,
    label    : 'Insurance Company Name',
    name     : FieldNames.insuranceCompanyName,
  },
  {
    id       : 1023,
    component: Address,
    label    : 'Street Address',
    name     : FieldNames.insuranceCompanyStreet,
    title    : 'Where is the insurance company located?',
  },
  {
    id        : 1024,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.insuranceCompanyStreet2,
  },
  {
    id       : 1025,
    component: Address,
    label    : 'City',
    name     : FieldNames.insuranceCompanyCity,
  },
  {
    id       : 1026,
    component: State,
    label    : 'State',
    name     : FieldNames.insuranceCompanyState,
  },
  {
    id       : 1027,
    component: ZipCode,
    label    : 'Zip Code',
    name     : FieldNames.insuranceCompanyZip,
  },
  {
    id       : 1028,
    component: Name,
    label    : 'First Name',
    name     : FieldNames.insuranceAgentFirstName,
    title    : 'Tell us about the insurance agent',
  },
  {
    id        : 1029,
    component : Name,
    isOptional: true,
    label     : 'Middle Name',
    name      : FieldNames.insuranceAgentMiddleName,
  },
  {
    id       : 1030,
    component: Name,
    label    : 'Last Name',
    name     : FieldNames.insuranceAgentLastName,
  },
  {
    id        : 1031,
    component : Select,
    isOptional: true,
    label     : 'Suffix',
    name      : FieldNames.insuranceAgentSuffixName,
    options   : NAME_SUFFIX_OPTIONS,
  },
  {
    id       : 1032,
    component: Email,
    label    : 'Email',
    name     : FieldNames.insuranceAgentEmail,
  },
  {
    id       : 1033,
    component: Phone,
    label    : 'Phone',
    name     : FieldNames.insuranceAgentPhone,
  },
  {
    id       : 1034,
    component: TextInput,
    label    : 'Trade Reference Name',
    name     : FieldNames.tradeReferenceName,
  },
  {
    id       : 1035,
    component: Address,
    label    : 'Street Address',
    name     : FieldNames.tradeReferenceStreet,
    title    : 'Where is the trade reference located?',
  },
  {
    id        : 1036,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.tradeReferenceStreet2,
  },
  {
    id       : 1037,
    component: Address,
    label    : 'City',
    name     : FieldNames.tradeReferenceCity,
  },
  {
    id       : 1038,
    component: State,
    label    : 'State',
    name     : FieldNames.tradeReferenceState,
  },
  {
    id       : 1039,
    component: ZipCode,
    label    : 'Zip Code',
    name     : FieldNames.tradeReferenceZip,
  },
  {
    id       : 1040,
    component: Phone,
    label    : 'Phone',
    name     : FieldNames.tradeReferencePhone,
  },
  {
    id       : 1041,
    component: Email,
    label    : 'Email',
    name     : FieldNames.tradeReferenceEmail,
  },
  {
    id       : 1042,
    component: TextInput,
    label    : 'Contact',
    name     : FieldNames.tradeReferenceContact,
  },
];
